import * as duckdb from '@duckdb/duckdb-wasm'
import eh_worker from '@duckdb/duckdb-wasm/dist/duckdb-browser-eh.worker.js?url'
import mvp_worker from '@duckdb/duckdb-wasm/dist/duckdb-browser-mvp.worker.js?url'
import duckdb_wasm_eh from '@duckdb/duckdb-wasm/dist/duckdb-eh.wasm?url'
import duckdb_wasm from '@duckdb/duckdb-wasm/dist/duckdb-mvp.wasm?url'
import React, { createContext, useEffect, useState } from 'react'

/**
 * Returns an initialized instance of DuckDB-Wasm, or `undefined` if
 * initialization has not completed yet.
 */
export const DuckDbInstanceContext = createContext<{
  duckDbInstance: duckdb.AsyncDuckDB | undefined
}>({
  duckDbInstance: undefined,
})

/**
 * Initializes a single instance of DuckDB-Wasm which individual components can
 * simply connect to. This is required to prevent out of memory errors that could
 * happen if too many instances of DuckDB are initialized at once.
 *
 * Note: DuckDB is an in-process analytical database which we are using to read data
 * out of parquet files. It should NOT be confused with Rolodex, which is still our
 * overall data store and single source of truth for data.
 */
export const DuckDbInstanceProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [duckDbInstance, setDuckDbInstance] = useState<
    duckdb.AsyncDuckDB | undefined
  >(undefined)

  useEffect(() => {
    const initializeDuckDb = async () => {
      const MANUAL_BUNDLES: duckdb.DuckDBBundles = {
        mvp: {
          mainModule: duckdb_wasm,
          mainWorker: mvp_worker,
        },
        eh: {
          mainModule: duckdb_wasm_eh,
          mainWorker: eh_worker,
        },
      }
      const bundle = await duckdb.selectBundle(MANUAL_BUNDLES)
      const worker = new Worker(bundle.mainWorker!)
      const logger = new duckdb.ConsoleLogger()

      const db = new duckdb.AsyncDuckDB(logger, worker)
      await db.instantiate(bundle.mainModule, bundle.pthreadWorker)
      setDuckDbInstance(db)
    }
    initializeDuckDb()
  }, [])

  return (
    <DuckDbInstanceContext.Provider value={{ duckDbInstance }}>
      {!duckDbInstance ? null : children}
    </DuckDbInstanceContext.Provider>
  )
}
